import React, { useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import ReactPixel from "react-facebook-pixel";

function AZNationalContactForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [subject, setSubject] = useState("");
  const [customSubject, setCustomSubject] = useState("");
  const [message, setMessage] = useState("");
  const [captchaValue, setCaptchaValue] = useState(null);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const subDomain = window.location.hostname.split(".")[0];
  const recaptchaKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const apiUrl = process.env.REACT_APP_API_URL;

  // Arizona National specific subjects
  const subjects = [
    "Fuel Program Information",
    "Supporter Questions",
    "Card Ordering",
    "Account Support",
    "Partnership Opportunities",
    "Scholarship Program Funding",
    "Technical Issues",
    "Billing Questions",
    "Other",
  ];
  
  const formatPhoneNumber = (input) => {
    // Remove all non-numeric characters
    const cleaned = input.replace(/\D/g, "");
    // Apply formatting (123) 456-7890
    if (cleaned.length <= 3) {
      return `(${cleaned}`;
    } else if (cleaned.length <= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
  };

  // Handle phone number input change
  const handlePhoneChange = (e) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    setPhone(formattedPhone);
  };

  // Validate phone number format before submission
  const isValidPhoneNumber = (phone) => {
    const cleaned = phone.replace(/\D/g, ""); // Remove non-numeric chars
    return cleaned.length === 10; // Ensure it's exactly 10 digits
  };

  // Handle reCAPTCHA verification
  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset messages
    setError("");
    setSuccessMessage("");

    // Validate reCAPTCHA
    if (!captchaValue) {
      setError("Please complete the reCAPTCHA verification.");
      return;
    }

    // Validate phone number
    if (!isValidPhoneNumber(phone)) {
      setError("Please enter a valid phone number in the format: (123) 456-7890");
      return;
    }

    ReactPixel.track("Contact");

    setLoading(true);

    // Determine the final subject
    const finalSubject = subject === "Other" ? customSubject : subject;

    // Prepare payload
    const payload = {
      name,
      email,
      phone,
      subject: finalSubject,
      message,
      recaptchaToken: captchaValue,
      subdomain: subDomain,
    };

    try {
      // Send form data to backend
      const response = await fetch(`${apiUrl}/dataServer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "handleContactFormSubmission",
          payload: payload,
        }),
      });

      const data = await response.json(); // Parse response JSON

      if (!data.success) {
        throw new Error(data.message || "Failed to submit the form.");
      }

      // Success message
      setSuccessMessage("Thank you! Your message has been sent successfully. We will be in touch shortly regarding the Arizona National Fuel Program.");

      // Clear form after submission
      setName("");
      setEmail("");
      setPhone("");
      setSubject("");
      setCustomSubject("");
      setMessage("");
      setCaptchaValue(null);
    } catch (error) {
      console.error("Error:", error);
      setError("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {successMessage && (
        <div className="mb-8 p-4 bg-green-50 border-l-4 border-green-400 text-green-800 rounded">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm font-medium">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Full Name *
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8872b] focus:border-[#c8872b] outline-none transition-colors"
              required
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8872b] focus:border-[#c8872b] outline-none transition-colors"
              required
            />
          </div>
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number *
          </label>
          <input
            type="text"
            id="phone"
            value={phone}
            onChange={handlePhoneChange}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8872b] focus:border-[#c8872b] outline-none transition-colors"
            placeholder="(123) 456-7890"
            required
          />
        </div>

        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
            What can we help you with? *
          </label>
          <select
            id="subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8872b] focus:border-[#c8872b] outline-none transition-colors"
            required
          >
            <option value="" disabled>Select a topic</option>
            {subjects.map((subj) => (
              <option key={subj} value={subj}>{subj}</option>
            ))}
          </select>
        </div>

        {/* Custom Subject Input (only if "Other" is selected) */}
        {subject === "Other" && (
          <div>
            <label htmlFor="customSubject" className="block text-sm font-medium text-gray-700 mb-2">
              Please specify *
            </label>
            <input
              type="text"
              id="customSubject"
              value={customSubject}
              onChange={(e) => setCustomSubject(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8872b] focus:border-[#c8872b] outline-none transition-colors"
              placeholder="Enter your topic"
              required
            />
          </div>
        )}

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
            Message *
          </label>
          <textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8872b] focus:border-[#c8872b] outline-none transition-colors resize-none"
            rows="6"
            placeholder="Please provide details about your inquiry..."
            required
          ></textarea>
        </div>

        {/* reCAPTCHA v2 Checkbox */}
        <div className="flex justify-center">
          <ReCAPTCHA sitekey={recaptchaKey} onChange={handleCaptchaChange} />
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-4 bg-red-50 border-l-4 border-red-400 text-red-800 rounded">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="text-center">
          <button 
            type="submit" 
            className="bg-[#c8872b] hover:bg-[#a06d1f] text-white font-semibold py-3 px-8 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mx-auto" 
            disabled={!captchaValue || loading}
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  />
                </svg>
                Sending Message...
              </>
            ) : (
              "Send Message"
            )}
          </button>
        </div>
      </form>

      <div className="mt-8 text-center text-sm text-gray-600">
        <p>Have questions about our fuel program? We're here to help!</p>
        <p className="mt-1">Typical response time: 1-2 business days</p>
      </div>
    </div>
  );
}

export default AZNationalContactForm;